import * as React from "react";

import { Box } from "../../components/ui/box";
import { cn } from "../../lib/utils";

import {
  MIN_COLUMN_WIDTH,
  STRUCTURAL_COLUMN_IDS,
  TABLE_TOTAL_WIDTH_VAR,
  columnLeftVar,
  columnWidthVar,
} from "./data-table-utils";
import type { Omittable } from "../../lib/props";

// ---------------------------------------------------------------------------
// useColumnResize — drag controller for column resizing
// ---------------------------------------------------------------------------
//
// Width = clamp(startWidth + (clientX − startX)) — absolute math from a
// drag-start snapshot of the resolved width map, so the dragged boundary
// tracks the pointer 1:1 and clamped overshoot recovers without drift.
//
// Per-frame updates write CSS variables on the scroll container imperatively
// (the dragged column's width var, downstream pinned left-offset vars, and
// the table total) — zero React renders per frame. State commits once on
// pointer-up via onCommit.
//
// First resize bakes the table: when the sizing map is empty, the resolved
// map (minus structural columns AND minus the absorber column) is committed
// alongside the dragged width. Baked values equal the currently rendered
// widths, so nothing moves — it freezes auto flex so no non-absorber column
// ever reflows. The absorber is deliberately left unbaked: it is the elastic
// column, and persisting its container-dependent stretched width would
// freeze it (and change which column flexes on the next load).
//
// The absorber must sit visually AFTER the dragged column. Shrinking a
// column on the other side of the dragged boundary shifts everything between
// it and the boundary, so the edge under the pointer wouldn't move — resize
// reads as the dragged column "growing out of the left". When the designated
// fill column is at or before the dragged one (e.g. a pinned first column as
// fill), the visually-last consumer column absorbs instead; the fill column
// then IS baked at its rendered (stretched) width, which both keeps the
// commit re-render identical to the last drag frame and drops the fill
// designation (sizing[fillColumnId] set → data-table falls back to the
// visually-last column), so subsequent drags stay well-behaved.
//
// Dragging the visually-last consumer column itself has nothing after it to
// absorb, so it absorbs its own deficit: it renders at natural + deficit
// (glued to the container edge while shrinking, tracking the pointer while
// growing) and commits the natural width; data-table's fill fallback then
// re-applies the same stretch on the next resolve, so nothing jumps.

// Same edge zone and speed as the header-reorder drag, so the two column
// gestures scroll the box identically.
const EDGE_SCROLL_ZONE_PX = 40;
const EDGE_SCROLL_SPEED_PX = 14;

export type ColumnResizeController = {
  startResize: (columnId: string, clientX: number) => void;
  moveResize: (clientX: number) => void;
  endResize: () => void;
  clearColumnSize: (columnId: string) => void;
};

type ActiveDrag = {
  columnId: string;
  startX: number;
  startWidth: number;
  startTotal: number;
  /** Full-map snapshot to commit when this drag is the first customization. */
  bake: Record<string, number> | null;
  /** Left-pinned columns after the dragged one — their sticky offsets follow the drag. */
  downstreamPinned: { id: string; startOffset: number }[];
  /**
   * The drag's absorber column — absorbs any width deficit per frame so the
   * table edge stays glued to the container while shrinking. The designated
   * fill column when it sits visually after the dragged one, otherwise the
   * visually-last consumer column (see the header comment). baseWidth is the
   * unstretched width; startStretch is the stretch already applied at drag
   * start (resolved − base), so the natural total can be recovered.
   */
  fill: { id: string; baseWidth: number; startStretch: number };
  containerWidth: number;
  lastWidth: number;
  raf: number | null;
  pendingClientX: number | null;
  el: HTMLElement;
  prevCursor: string;
  prevUserSelect: string;
  /**
   * Scroll offset at drag start. A non-pinned column's dragged boundary lives
   * in content coordinates, so scroll travel folds into the width — that is
   * what lets the edge auto-scroll below keep growing a column whose seam
   * has hit the viewport edge. A left-pinned column's boundary is sticky
   * (screen-fixed), so its drag ignores scroll entirely.
   */
  startScrollLeft: number;
  lastClientX: number;
  /** Edge auto-scroll rAF loop id; null for a pinned drag, which has none. */
  edgeRaf: number | null;
  tracksScroll: boolean;
};

export function useColumnResize({
  scrollContainerRef,
  widths,
  pinnedLeftIds,
  sizing,
  fillColumn,
  onCommit,
}: Omittable<{
  scrollContainerRef: React.RefObject<HTMLElement | null>;
  widths: Map<string, number>;
  pinnedLeftIds: string[];
  sizing: Record<string, number>;
  /** The table's fill column + its unstretched width (see ActiveDrag.fill). */
  fillColumn?: { id: string; baseWidth: number } | null;
  onCommit: (sizing: Record<string, number>) => void;
}>): {
  controller: ColumnResizeController;
  activeResizeColumnId: string | null;
} {
  const widthsRef = React.useRef(widths);
  widthsRef.current = widths;
  const pinnedLeftIdsRef = React.useRef(pinnedLeftIds);
  pinnedLeftIdsRef.current = pinnedLeftIds;
  const sizingRef = React.useRef(sizing);
  sizingRef.current = sizing;
  const fillColumnRef = React.useRef(fillColumn);
  fillColumnRef.current = fillColumn;
  const onCommitRef = React.useRef(onCommit);
  onCommitRef.current = onCommit;

  const dragRef = React.useRef<ActiveDrag | null>(null);
  const [activeResizeColumnId, setActiveResizeColumnId] = React.useState<
    string | null
  >(null);

  const applyFrame = React.useCallback(() => {
    const drag = dragRef.current;
    // Backstop against frame timing: `endResize` cancels the queued frame and
    // then clears the drag snapshot, but a cancel cannot un-queue a callback
    // the browser has already begun dispatching. Re-entering with no drag, or
    // with the pending position already flushed, must be a no-op rather than
    // a second write from a stale snapshot.
    if (!drag || drag.pendingClientX === null) return;
    drag.raf = null;
    const clientX = drag.pendingClientX;
    drag.pendingClientX = null;

    // The dragged boundary lives in content coordinates, so scroll travel
    // since drag start is part of the width — this is what makes the edge
    // auto-scroll grow the column while the pointer holds still at the
    // viewport edge. Pinned boundaries are screen-fixed and take none of it.
    const scrollDelta = drag.tracksScroll
      ? drag.el.scrollLeft - drag.startScrollLeft
      : 0;
    const next = Math.max(
      MIN_COLUMN_WIDTH,
      drag.startWidth + (clientX - drag.startX) + scrollDelta,
    );
    drag.lastWidth = next;

    const style = drag.el.style;
    const delta = next - drag.startWidth;
    // Mirror the commit-time fill: recover the natural (unstretched) total,
    // give the absorber exactly the remaining deficit, never less than its
    // base. Keeps the table edge glued to the container mid-drag with no
    // jump on release. A self-absorbing drag (last consumer column) renders
    // at natural + deficit in one write; `lastWidth` stays the natural width
    // either way, so the commit never persists a container-dependent value.
    const naturalTotal = drag.startTotal + delta - drag.fill.startStretch;
    const deficit = Math.max(0, drag.containerWidth - naturalTotal);
    if (drag.fill.id === drag.columnId) {
      style.setProperty(columnWidthVar(drag.columnId), `${next + deficit}px`);
    } else {
      style.setProperty(columnWidthVar(drag.columnId), `${next}px`);
      style.setProperty(
        columnWidthVar(drag.fill.id),
        `${drag.fill.baseWidth + deficit}px`,
      );
    }
    style.setProperty(TABLE_TOTAL_WIDTH_VAR, `${naturalTotal + deficit}px`);
    for (const { id, startOffset } of drag.downstreamPinned) {
      style.setProperty(columnLeftVar(id), `${startOffset + delta}px`);
    }
  }, []);

  const startResize = React.useCallback(
    (columnId: string, clientX: number) => {
      // The one and only caller is `DataTableResizeHandle`, which data-table
      // renders once per resizable, measured, non-structural column, inside
      // the scroll container. Three invariants follow, and each replaces a
      // guard that no caller could trip:
      //  - `columnId` is never structural (`canResize` excludes those),
      //  - it is always a key of the resolved width map (the header cells and
      //    that map are both built from `visualLayout.visualIds`),
      //  - the scroll container is mounted, because the handle is inside it.
      const currentWidths = widthsRef.current;
      const startWidth = currentWidths.get(columnId)!;
      const el = scrollContainerRef.current as HTMLElement;

      let startTotal = 0;
      for (const w of currentWidths.values()) startTotal += w;

      const currentFill = fillColumnRef.current;
      // Pick the drag's absorber: the designated fill column only when it
      // sits visually after the dragged one — shrinking a column before the
      // dragged boundary would move the edge under the pointer (see the
      // header comment). Otherwise the visually-last consumer column after
      // the dragged one absorbs. currentWidths preserves visual order (left
      // pinned → center → right pinned), so key index IS visual position.
      const visualIds = [...currentWidths.keys()];
      const draggedIndex = visualIds.indexOf(columnId);
      let found: { id: string; baseWidth: number } | null = null;
      if (
        currentFill &&
        currentFill.id !== columnId &&
        visualIds.indexOf(currentFill.id) > draggedIndex
      ) {
        found = currentFill;
      } else {
        for (let i = visualIds.length - 1; i > draggedIndex; i--) {
          const id = visualIds[i];
          if (id === undefined || STRUCTURAL_COLUMN_IDS.has(id)) continue;
          // Not the designated fill, so its resolved width carries no
          // stretch — the map value is the unstretched base.
          found = { id, baseWidth: currentWidths.get(id)! };
          break;
        }
      }
      // No consumer column after the dragged one (it is the visually-last):
      // the dragged column absorbs its own deficit. Its rendered width at
      // drag start — stretch included — is the base, so the edge tracks the
      // pointer 1:1 while growing and stays glued to the container edge when
      // shrinking below fill (a narrower total would only open a gap the row
      // background hides).
      const absorber = found ?? { id: columnId, baseWidth: startWidth };

      const currentSizing = sizingRef.current;
      let bake: Record<string, number> | null = null;
      if (Object.keys(currentSizing).length === 0) {
        bake = {};
        for (const [id, w] of currentWidths) {
          // Skip the absorber — its resolved width may include a
          // container-dependent stretch, and persisting that would freeze
          // the table's elastic column (see the header comment). A fill
          // column that is NOT the absorber is baked at its rendered width
          // on purpose: that freezes it and hands fill duty to the
          // visually-last column from the commit onward.
          if (STRUCTURAL_COLUMN_IDS.has(id) || id === absorber.id) {
            continue;
          }
          bake[id] = Math.round(w);
        }
      }

      const downstreamPinned: { id: string; startOffset: number }[] = [];
      const leftIds = pinnedLeftIdsRef.current;
      const draggedPinnedIndex = leftIds.indexOf(columnId);
      if (draggedPinnedIndex !== -1) {
        let offset = 0;
        for (const [i, leftId] of leftIds.entries()) {
          if (i > draggedPinnedIndex) {
            downstreamPinned.push({ id: leftId, startOffset: offset });
          }
          // Left-pinned ids are a subset of the map's keys, so no fallback.
          offset += currentWidths.get(leftId)!;
        }
      }

      const fill = {
        id: absorber.id,
        baseWidth: absorber.baseWidth,
        startStretch: currentWidths.get(absorber.id)! - absorber.baseWidth,
      };

      const tracksScroll = draggedPinnedIndex === -1;
      const body = document.body;
      dragRef.current = {
        columnId,
        startX: clientX,
        startWidth,
        startTotal,
        bake,
        downstreamPinned,
        fill,
        containerWidth: el.clientWidth,
        lastWidth: startWidth,
        raf: null,
        pendingClientX: null,
        el,
        prevCursor: body.style.cursor,
        prevUserSelect: body.style.userSelect,
        startScrollLeft: el.scrollLeft,
        lastClientX: clientX,
        edgeRaf: null,
        tracksScroll,
      };
      body.style.cursor = "col-resize";
      body.style.userSelect = "none";
      setActiveResizeColumnId(columnId);

      // Edge auto-scroll: growing a column pushes its seam to the viewport
      // edge long before the resize is done, so holding the pointer inside
      // the edge zone scrolls the box — and the scroll travel folds into the
      // width (see applyFrame), so the column keeps resizing without a
      // release-scroll-regrab cycle. Pinned drags skip the loop: their
      // boundary is screen-fixed and gains nothing from scrolling.
      if (tracksScroll) {
        const edgeTick = () => {
          const active = dragRef.current;
          if (!active) return;
          active.edgeRaf = requestAnimationFrame(edgeTick);
          const rect = active.el.getBoundingClientRect();
          const before = active.el.scrollLeft;
          if (active.lastClientX < rect.left + EDGE_SCROLL_ZONE_PX) {
            active.el.scrollLeft -= EDGE_SCROLL_SPEED_PX;
          } else if (active.lastClientX > rect.right - EDGE_SCROLL_ZONE_PX) {
            active.el.scrollLeft += EDGE_SCROLL_SPEED_PX;
          }
          // Only a frame that actually scrolled re-applies; a pointer parked
          // mid-container costs nothing per frame.
          if (active.el.scrollLeft !== before) {
            active.pendingClientX = active.lastClientX;
            applyFrame();
          }
        };
        dragRef.current.edgeRaf = requestAnimationFrame(edgeTick);
      }
    },
    [scrollContainerRef, applyFrame],
  );

  const moveResize = React.useCallback(
    (clientX: number) => {
      const drag = dragRef.current;
      if (!drag) return;
      drag.lastClientX = clientX;
      drag.pendingClientX = clientX;
      // One frame per batch: the else arm is every extra pointermove the
      // browser delivers inside a frame that has not painted yet, and it is
      // the whole point of the batch — the newest position wins and no
      // second frame is scheduled.
      if (drag.raf === null) {
        drag.raf = requestAnimationFrame(applyFrame);
      }
    },
    [applyFrame],
  );

  const endResize = React.useCallback(() => {
    const drag = dragRef.current;
    if (!drag) return;
    if (drag.edgeRaf !== null) cancelAnimationFrame(drag.edgeRaf);
    // Both guards below are frame-timing backstops, and both fire only when
    // the release lands in a frame the last move had already painted: a
    // release that arrives in the SAME frame as its last move still has a
    // queued rAF to cancel and an unpainted position to flush.
    if (drag.raf !== null) cancelAnimationFrame(drag.raf);
    if (drag.pendingClientX !== null) {
      drag.raf = null;
      applyFrame();
    }
    dragRef.current = null;
    document.body.style.cursor = drag.prevCursor;
    document.body.style.userSelect = drag.prevUserSelect;
    setActiveResizeColumnId(null);

    // A press that never moved has nothing to persist — committing here
    // would bake the whole table on a stray click.
    if (drag.lastWidth === drag.startWidth) return;

    const base = drag.bake ?? { ...sizingRef.current };
    base[drag.columnId] = Math.round(drag.lastWidth);
    onCommitRef.current(base);
  }, [applyFrame]);

  const clearColumnSize = React.useCallback((columnId: string) => {
    const next = { ...sizingRef.current };
    if (!(columnId in next)) return;
    delete next[columnId];
    onCommitRef.current(next);
  }, []);

  const controller = React.useMemo<ColumnResizeController>(
    () => ({ startResize, moveResize, endResize, clearColumnSize }),
    [startResize, moveResize, endResize, clearColumnSize],
  );

  return { controller, activeResizeColumnId };
}

// ---------------------------------------------------------------------------
// DataTableResizeHandle — the grabbable edge in each header cell
// ---------------------------------------------------------------------------

const HANDLE_HIT_WIDTH = 9;
const HANDLE_LINE_WIDTH = 2;
const DOUBLE_PRESS_MS = 350;
const PRESS_SLOP_PX = 3;

/**
 * Pointer-capture drag handle, so the drag keeps reporting through fast
 * pointer travel. Sits inside the header cell's right edge (cells clip
 * overflow), above the sort button, and stops propagation so a resize never
 * sorts or starts a header reorder. Two quick clicks without movement clear
 * the column's override (detected from pointer-up timing so a drag never
 * counts as a click).
 *
 * The boundary line is revealed per column, not per row: each header cell is
 * a `group/hcell` (and carries `data-dthcell`), and hovering a column shows
 * only that column's edges — its own cell's hover reveals the right edge,
 * and a `:has(+ :hover)` selector on the preceding cell reveals the left one
 * (CSS has no previous-sibling selector). The handle's own `group/resize`
 * turns the line under the pointer to the accent color. `active` keeps it
 * accent-colored during the drag, when pointer capture makes :hover
 * unreliable.
 */
export function DataTableResizeHandle({
  columnId,
  controller,
  active,
}: {
  columnId: string;
  controller: ColumnResizeController;
  active: boolean;
}): React.ReactElement {
  const downXRef = React.useRef(0);
  const movedRef = React.useRef(false);
  const lastTapAtRef = React.useRef(0);

  return (
    <Box group="resize" touchAction="none" userSelect="none"
      data-dtresize="true"
      data-testid={`data-table-resize-handle-${columnId}`}
      position="absolute"
      top={0}
      bottom={0}
      right={0}
      width={HANDLE_HIT_WIDTH}
      zIndex={20}
      cursor="col-resize"
      onPointerDown={(e: React.PointerEvent<HTMLDivElement>) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        e.currentTarget.setPointerCapture?.(e.pointerId);
        downXRef.current = e.clientX;
        movedRef.current = false;
        controller.startResize(columnId, e.clientX);
      }}
      onPointerMove={(e: React.PointerEvent<HTMLDivElement>) => {
        if (Math.abs(e.clientX - downXRef.current) > PRESS_SLOP_PX) {
          movedRef.current = true;
        }
        controller.moveResize(e.clientX);
      }}
      onPointerUp={(e: React.PointerEvent<HTMLDivElement>) => {
        try {
          e.currentTarget.releasePointerCapture?.(e.pointerId);
        } catch {
          // releasing an uncaptured pointer throws in some browsers
        }
        controller.endResize();
        if (!movedRef.current) {
          const now = Date.now();
          if (now - lastTapAtRef.current < DOUBLE_PRESS_MS) {
            lastTapAtRef.current = 0;
            controller.clearColumnSize(columnId);
          } else {
            lastTapAtRef.current = now;
          }
        }
      }}
      onPointerCancel={() => controller.endResize()}
    >
      <Box hoverReveal="resize" pointerEvents="none" background="border"
        className={cn("",
          // Own column hovered → this line is its right edge.
          "group-hover/hcell:opacity-0",
          // Next column hovered → this line is its left edge. No CSS
          // previous-sibling selector exists, so :has() on the owning cell.
          "[[data-dthcell]:has(+[data-dthcell]:hover)_&]:opacity-0",
          "group-hover/resize:bg-primary",
          active && "bg-primary opacity-100",
        )}
        position="absolute"
        top={0}
        bottom={0}
        left={HANDLE_HIT_WIDTH - HANDLE_LINE_WIDTH - 1}
        width={HANDLE_LINE_WIDTH}
        style={{
          transition: "opacity 150ms ease, background-color 150ms ease",
        }}
      />
    </Box>
  );
}
